import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { z } from "zod";
import { auditInfinite } from "../../api/admin";
import type { AuditEntry } from "../../api/types";
import { requireAdmin } from "../../auth/useMe";
import { DataTable } from "../../components/DataTable";
import { Button, Card, EmptyState, ErrorState, Loading, PageHeader, Select } from "../../components/ui";
import { fmtDateTime } from "../../lib/format";
import { useNavigate } from "../../lib/navigate";

const searchSchema = z.object({
  action: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/admin/audit")({
  staticData: { crumb: { label: "Audit log", parents: [] } },
  validateSearch: (s) => searchSchema.parse(s),
  beforeLoad: requireAdmin,
  component: AuditPage,
});

const ACTION_GROUPS = [
  { value: "auth.", label: "Sign-ins" },
  { value: "user.", label: "Users" },
  { value: "provider.", label: "Providers and secrets" },
  { value: "policy.", label: "Policies" },
  { value: "number.", label: "Numbers" },
  { value: "brand.", label: "Brands" },
  { value: "recording.", label: "Recording access" },
  { value: "export.", label: "Exports" },
];

const col = createColumnHelper<AuditEntry>();

function detailText(d: Record<string, unknown> | undefined): string {
  if (!d) return "";
  return Object.entries(d)
    .map(
      ([k, v]) =>
        `${k.replace(/_/g, " ")}: ${Array.isArray(v) ? v.join(", ") : typeof v === "object" ? JSON.stringify(v) : String(v)}`,
    )
    .join("; ");
}

function AuditPage() {
  const { action } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const q = useInfiniteQuery(auditInfinite(action));
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const columns = useMemo(
    () => [
      col.accessor("at", {
        header: "When",
        enableSorting: false,
        cell: (c) => <span className="tnum whitespace-nowrap">{fmtDateTime(c.getValue())}</span>,
      }),
      col.accessor((a) => a.actor?.name ?? "System", {
        id: "actor",
        header: "Who",
        enableSorting: false,
      }),
      col.accessor("action", {
        header: "Action",
        enableSorting: false,
        cell: (c) => <span className="font-mono text-caption">{c.getValue()}</span>,
      }),
      col.accessor("target", { header: "Target", enableSorting: false }),
      col.accessor((a) => detailText(a.detail), {
        id: "detail",
        header: "Detail",
        enableSorting: false,
        cell: (c) => <span className="text-ink-2">{c.getValue()}</span>,
      }),
      col.accessor("ip", {
        header: "IP address",
        enableSorting: false,
        cell: (c) => <span className="tnum text-ink-2">{c.getValue() ?? ""}</span>,
      }),
    ],
    [],
  );

  return (
    <>
      <PageHeader title="Audit log" />
      <div className="mb-3">
        <Select
          aria-label="Action"
          className="w-56"
          value={action ?? ""}
          onValueChange={(v) =>
            navigate({
              search: { action: v || undefined },
              replace: true,
            })
          }
          options={[
            { value: "", label: "All actions" },
            ...(ACTION_GROUPS ?? []).map((g) => ({ value: g.value, label: g.label })),
          ]}
        />
      </div>
      <Card>
        {q.isPending ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        ) : (
          <>
            <DataTable
              data={rows}
              columns={columns}
              getRowId={(a) => a.id}
              empty={<EmptyState title="Nothing recorded yet" />}
            />
            {q.hasNextPage ? (
              <div className="flex justify-center border-t border-hairline p-3">
                <Button size="sm" onClick={() => q.fetchNextPage()} busy={q.isFetchingNextPage}>
                  Load more
                </Button>
              </div>
            ) : null}
          </>
        )}
      </Card>
    </>
  );
}
